import Controller from '@ember/controller';
import { beebeeAjaxGet } from 'beebee-base/lib/utility';
import moment from 'moment-timezone';

import { action, computed, set } from '@ember/object';

import { task, timeout } from 'ember-concurrency';

import { inject as iService } from '@ember/service';

import { inject as iController } from '@ember/controller';

import getConfig from 'beebee-base/lib/get-config';
export default class SciclubController extends Controller {
  @iService notify;
  queryParams = ['contact_id', 'project_id'];
  @iService router;
  @iService intl;
  @iService session;
  @iService store;
  @iService log;
  @iController('application') applicationController;

  @computed('from_date')
  get annoCorrente() {
    if (!this.from_date) {
      return '';
    }

    return `${moment(this.from_date).format('YYYY')} - ${moment(this.from_date).add(1, 'years').format('YYYY')}`;
  }

  @(task(function* () {
    yield timeout(250);

    if (!this.session.token) {
      return;
    }

    if (!this.session.option_field) {
      // Bounded retry: option_field loads async; wait for it with backoff
      // instead of unconditional self-recursion (which spins forever if it
      // never loads). Cap at 20 attempts (~10s total) then bail.
      const maxRetries = 20;
      let attempt = 0;
      while (!this.session.option_field && attempt < maxRetries) {
        attempt++;
        yield timeout(500);
        if (!this.session.token) {
          return;
        }
      }
      if (!this.session.option_field) {
        return;
      }
    }

    if (!this.from_date || !this.to_date) {
      // A club that has never configured its season dates arrives here with
      // `segreteria_from_date`/`segreteria_to_date` undefined.
      // `moment(undefined, 'DD/MM/YYYY')` is an INVALID moment and `.format()`
      // on it returns the literal string "Invalid date" — which used to be
      // assigned straight to `from_date`/`to_date` and sent on every portal
      // query. The guard above then saw a truthy value and never tried again,
      // so the portal was permanently pinned to a window the backend cannot
      // parse, the season label read "Invalid date - Invalid date", and both
      // season arrows were dead (`moment('Invalid date').add(1, 'years')` is
      // still invalid). Leave the window UNSET instead: `bbParam` drops
      // undefined keys, so the request simply omits the dates and the backend
      // applies its own default.
      const from = moment(this.session.option_field?.segreteria_from_date, 'DD/MM/YYYY');
      const to = moment(this.session.option_field?.segreteria_to_date, 'DD/MM/YYYY');

      if (from.isValid() && to.isValid()) {
        set(this, 'from_date', from.format('YYYY-MM-DDTHH:mm:ssZ'));
        set(this, 'to_date', to.format('YYYY-MM-DDTHH:mm:ssZ'));
      }
    }

    try {
      const customData = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/sciclub_personal_area`,
        this.session.token,
        {
          from_date: this.from_date,
          to_date: this.to_date,
        }
      );

      const dataApi = customData?.data?.[0];
      set(this, 'customData', dataApi);

      if (this.detailAtleta?.atleta_id && dataApi) {
        const atleta = dataApi.atleti.find((i) => i.atleta_id === this.detailAtleta?.atleta_id);
        if (atleta) {
          set(this, 'detailAtleta', atleta);
        }
      }
    } catch (e) {
      this.log.warn('loadDataTask: load failed', e);
    }
  }).restartable())
  loadDataTask;

  @(task(function* (contact_id) {
    yield timeout(100);

    const c = yield this.store.findRecord('contact', contact_id, {
      reload: true,
    });

    set(this, 'editContact', c);
  }).restartable())
  editDataTask;

  @(task(function* (contact) {
    const requiredFields = ['first_name', 'last_name', 'cod_fiscale'];

    if (contact.id && parseInt(contact.id) === this.customData?.contatto?.contact_id) {
      requiredFields.push('mail', 'mobile');
    } else {
      requiredFields.push('luogo_nascita', 'data_nascita');
    }

    const contatto = contact || {};
    const address = contatto.get('addresses')?.[0];

    const saveErrors = {};

    // Controllo campi base
    requiredFields.forEach((field) => {
      const value = contatto.get(field);
      if (!value || value.trim() === '') {
        saveErrors[field] = field;
      }
    });

    // Controllo campi dell'indirizzo, se richiesti
    if (contact.id && parseInt(contact.id) === this.customData?.contatto?.contact_id) {
      const addressFields = [
        'location_address_street',
        'location_address_postal_code',
        'location_address_house',
        'location_address_country',
        'location_address_city',
      ];

      if (!address) {
        saveErrors.address = true;
      } else {
        addressFields.forEach((field) => {
          const value = address.get(field);
          if (!value || value.trim() === '') {
            saveErrors.address = true;
          }
        });
      }
    }

    set(contact, 'saveErrors', null);
    if (Object.keys(saveErrors).length > 0) {
      set(contact, 'saveErrors', saveErrors);
      this.notify.warning('Completa i dati anagrafici');
      return false;
    }

    const result = yield this.applicationController.saveRecordRelated(contact);

    // saveRecordRelated resolves `false` on failure — it never rejects. Closing
    // the editor unconditionally threw away the anagrafica the user had just
    // typed while the server had accepted nothing; saveRecordRelated has
    // already surfaced the error, so just keep the form open with the input.
    if (result === false) {
      return false;
    }

    yield this.loadDataTask.perform();

    set(this, 'editContact', null);
  }).restartable())
  saveContactTask;

  @action
  showAtleta(atleta) {
    set(this, 'detailAtleta', atleta);
    set(this, 'showTab', 'atleti');
    set(this, 'showMenu', null);
  }

  // Shift the season window by `years` and re-query the portal.
  //
  // The window can legitimately be unset — a club that never configured its
  // season dates lets the backend pick the default (see loadDataTask). Anchoring
  // on an unset/unparseable value produced an invalid moment whose `.format()`
  // is the string "Invalid date", so both arrows were dead for those clubs and
  // every later query carried a window the backend cannot read. Anchor on the
  // current window when it is valid, otherwise on the season around today, so
  // the arrows always move to a real year.
  _shiftSeason(years) {
    const from = moment(this.from_date);
    const to = moment(this.to_date);

    const anchorFrom = from.isValid() ? from : moment().startOf('year');
    const anchorTo = to.isValid() ? to : moment().endOf('year');

    set(this, 'from_date', anchorFrom.add(years, 'years').format('YYYY-MM-DDTHH:mm:ssZ'));
    set(this, 'to_date', anchorTo.add(years, 'years').format('YYYY-MM-DDTHH:mm:ssZ'));
    this.loadDataTask.perform();
  }

  @action
  moveNextYear() {
    this._shiftSeason(1);
  }

  @action
  movePrevYear() {
    this._shiftSeason(-1);
  }

  @(task(function* () {
    try {
      yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/../logout?access_token=${this.session.token}`);
    } catch (e) {
      // Server-side logout failing must not block the client-side logout below
      this.log.warn('logoutTask: server logout call failed', e);
    }

    if (this.socket) {
      this.socket.emit('logout', {
        user: this.content?.me?.id,
        token: this.session.token,
      });
    }

    this.session.logout();
    this.store.unloadAll();

    this.router.transitionTo('index');
    yield timeout(100);
    window.location.reload(true);
  }).drop())
  logoutTask;

  @(task(function* (payment) {
    const requiredFields = [
      'first_name',
      'last_name',
      'mail',
      'mobile',
      'cod_fiscale',
      'location_address_street',
      'location_address_postal_code',
      'location_address_house',
      'location_address_country',
      'location_address_city',
    ];

    const contatto = this.customData?.contatto || {};
    const isEmptyOrNull = (field) => !contatto[field] || contatto[field].trim() === '';

    const incompleteFields = requiredFields.filter((field) => isEmptyOrNull(field));

    if (incompleteFields.length > 0) {
      set(this, 'showTab', 'profilo');
      this.notify.warning(
        'Completa i tuoi dati anagrafici prima di procedere col pagmamento'
      );
      return false;
    }

    if (!payment?.payment_id) return;

    const payStore = yield this.store.findRecord('payment', payment.payment_id, {
      reload: true,
    });

    if (!payStore) {
      return;
    }

    set(this, 'payNowData', {
      payment: payStore,
      contact_id: this.session.me.get('id'),
      request_by: 45322,
    });
  }).restartable())
  payNowTask;

  @action
  setTab(tab = null) {
    // Guard against the DOM event leaking in when used as a bare {{on "click"}} handler
    if (tab instanceof Event) {
      tab = null;
    }
    set(this, 'showTab', tab);
    set(this, 'showMenu', null);
  }
}
